import { getTranslatedText } from '../utils/translate';

export const ERROR_MESSAGE = {
  INVALID_EMAIL: getTranslatedText('이메일 형식이 올바르지 않습니다.'),
  EMAIL_NOT_EXIST: getTranslatedText('존재하지 않는 계정입니다.'),
  WRONG_EMAIL_ADDRESS: getTranslatedText('메일 주소를 다시 확인해 주세요.'),
  INVALID_PASSWORD: getTranslatedText('영문/숫자/특수문자 중, 2가지 이상 포함'),
  INVALID_PASSWORD_LANGUAGE: getTranslatedText(
    '영문 대소문자(다른 언어 불가능)/숫자/특수문자 중, 2가지 이상 포함',
  ),
  WRONG_PASSWORD_LENGTH: (min: number, max: number) => getTranslatedText('최대최소', { min, max }),
  CONTINUOUS_LETTERS: getTranslatedText('연속 3자 이상 동일한 문자/숫자 제외'),
  UNCONFIRMED_PASSWORD: () => getTranslatedText('비밀번호가 일치하지 않습니다.'),
  VERIFICATION_CODE: getTranslatedText('인증번호가 일치하지 않습니다.'),
  IS_NOT_KOREAN_NUMBER: getTranslatedText('휴대폰 번호 형식이 올바르지 않습니다.'),
  IS_NOT_EMPTY: getTranslatedText('휴대폰 번호를 입력해 주세요.'),
  OVER_MAX_COUNT: getTranslatedText('재전송은 최대 10회입니다. 10분 후 재 시도해 주세요.'),
  EXISTED_EMAIL_REQUEST: getTranslatedText('이미 가입된 이메일입니다.'),
  LOGIN_FAILED: getTranslatedText('이메일 또는 비밀번호를 확인해 주세요.'),
  COMMON_LENGTH: (min: number, max: number) => getTranslatedText('최대최소', { min, max }),
  INVALID_SLUG_FORMAT: getTranslatedText('영문 소문자와 숫자만 사용할 수 있습니다.'),
  INVALID_NICKNAME: getTranslatedText('사용할 수 없는 문자열이 포함되어 있습니다.'),
  INVALID_EMOJI: getTranslatedText('이모티콘은 사용할 수 없습니다.'),
  TRY_AGAIN: getTranslatedText('에러가 발생했습니다. 다시 시도해 주세요.'),
  UNCHECK_CAPTCHA: getTranslatedText('가입 정보를 인증하고 있습니다. 잠시 후 다시 시도해주세요.'),
  INVALID_CAPTCHA_TOKEN: getTranslatedText(
    '인증에 실패했어요. 계속 오류가 발생하면 고객센터에 문의해 주시기 바랍니다.',
  ),
  ACCOUNT_NOT_VERIFIED: getTranslatedText('가입 인증이 완료되지 않은 계정입니다.'),

  // 파트너스 가입 아직 다국어 적용 x
  LENGTH: (min: number, max: number) => `${min}자 이상 ${max}자 이하로 입력해 주세요.`,
  INVALID_URL: () => 'https:// 를 포함하여 url 형식에 맞게 입력해 주세요.',
} as const;

export const HELP_MESSAGE = {
  INPUT_RETRY: (target: string) =>
    getTranslatedText('확인을 위해 {{target}} 다시 입력해 주세요.', {
      target: target.toLocaleLowerCase(),
    }),
};

export const SUCCESS_MESSAGE = {
  MATCH: (target: string) =>
    getTranslatedText('{{target}}와 일치합니다.', { target: target.toLocaleLowerCase() }),
};

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 32;

/**
 * 연속된 동일한 문자가 3개 미만인지 검사하는 정규표현식
 * - ^ : 문자열의 시작 부분을 의미합니다.
 * - (?! : 부정 전방 탐색(Negative Lookahead) 시작을 의미합니다.
 *   - .+ : 1개 이상의 모든 문자를 의미합니다.
 *   - \1: 첫 번째 캡처된 문자를 참조
 *   - \1\1: 첫 번째 캡처된 문자와 동일한 문자가 연속으로 2개 더 있는지 검사
 * - ) : 부정 전방 탐색 부분의 종료를 의미합니다.
 * - .+ : 1개 이상의 모든 문자를 의미합니다.
 * - $ : 문자열의 끝 부분을 의미합니다.
 */
export const REGEX_HAS_CONTINUOUS_LETTERS = /^(?!.*(.)\1\1).+$/;

/**
 * 영문자 포함 여부를 검사하는 정규표현식
 * - [a-zA-Z]: 영문자 대소문자 중 하나 이상 포함되어 있는지 검사
 */
export const HAS_ENG = /[a-zA-Z]/;

/**
 * 영어 대문자를 포함하는 정규표현식
 * - [A-Z]: 영어 대문자 중 하나 이상 포함되어 있는지 검사
 */
export const HAS_UPPERCASE = /[A-Z]/;

/**
 * 숫자 포함 여부를 검사하는 정규표현식
 * - [0-9]: 숫자 중 하나 이상 포함되어 있는지 검사
 */
export const HAS_NUMBER = /[0-9]/;

/**
 * 공백 포함 여부를 검사하는 정규표현식
 * - \s: 공백 문자 중 하나 이상 포함되어 있는지 검사
 */
export const HAS_SPACE = /\s/;

/**
 * 특수문자 포함 여부를 검사하는 정규표현식
 * - [\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]: ASCII 문자 중 특수문자에 해당하는 범위를 검사
 *   - \x21-\x2F: ! " # $ % & ' ( ) * + , - . /
 *   - \x3A-\x40: : ; < = > ? @
 *   - \x5B-\x60: [ \ ] ^ _ `
 *   - \x7B-\x7E: { | } ~
 */
export const HAS_SYMBOL = /[\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/;

/**
 * 유효한 비밀번호 문자를 검사하는 정규표현식
 * 영문,숫자,특수문자 이외의 문자 입력시 영문외의 다른 문자 입력 불가 em 노출
 */
export const INVALID_PASSWORD_LANGUAGE = /[^a-zA-Z0-9\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/;

/**
 * 한글 포함 여부를 검사하는 정규표현식
 */
export const HAS_KOR = /[가-힣ㄱ-ㅎㅏ-ㅣ]/g;

/**
 * 닉네임에서 사용할 수 없는 문자열을 검사하는 정규표현식
 */
export const HAS_INVALID_NICKNAME = /^[가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9`~!@#$%^&*()-_=+\s]+$/;

/**
 * 슬러그 형식 검사용: 소문자와 숫자만 포함된 문자열을 검사하는 정규표현식
 */
export const HAS_VALID_SLUG_FORMAT = /^[a-z0-9]+$/;

/**
 * 이모티콘을 포함한 문자열을 검사하는 정규표현식
 * 기존 /\p{Emoji_Presentation}/gu 은 하트와 같은 이모지를 허용하므로 미사용
 */
export const HAS_EMOJI =
  /[\u{1f300}-\u{1f5ff}\u{1f900}-\u{1f9ff}\u{1f600}-\u{1f64f}\u{1f680}-\u{1f6ff}\u{2600}-\u{26ff}\u{2700}-\u{27bf}\u{1f1e6}-\u{1f1ff}\u{1f191}-\u{1f251}\u{1f004}\u{1f0cf}\u{1f170}-\u{1f171}\u{1f17e}-\u{1f17f}\u{1f18e}\u{3030}\u{2b50}\u{2b55}\u{2934}-\u{2935}\u{2b05}-\u{2b07}\u{2b1b}-\u{2b1c}\u{3297}\u{3299}\u{303d}\u{00a9}\u{00ae}\u{2122}\u{23f3}\u{24c2}\u{23e9}-\u{23ef}\u{25b6}\u{23f8}-\u{23fa}]/gu;

export const NICKNAME_MIN_LENGTH = 2;
export const NICKNAME_MAX_LENGTH = 18;

export const SLUG_MIN_LENGTH = 1;
export const SLUG_MAX_LENGTH = 20;
